import { createApolloProvider } from '@vue/apollo-option';
import { createUploadLink } from 'apollo-upload-client';
import { cloneDeep, merge } from 'lodash-es';
import { omitDeep } from '@utils/omitDeep';
import Pusher from 'pusher-js';
import { ApolloClient, ApolloLink, InMemoryCache } from '@apollo/client/core';
import { onError } from '@apollo/client/link/error';
import { getUserToken } from '@boot/getUserToken';
import PusherLink from '@boot/pusherLink';
import { appStorage } from '@mobile/store/appStorage';
import { Preferences } from '@capacitor/preferences';

Preferences.configure({ group: 'NativeStorage' });

let $store = null;
let $router = null;

// HTTP connection to the API
const httpLink = createUploadLink({
  // You should use an absolute URL here
  uri: process.env.GRAPHQL_URI || 'http://localhost:8084/graphql',
});

const pusherLink = new PusherLink({
  pusher: new Pusher(process.env.PUSHER_APP_KEY, {
    channelAuthorization: {
      endpoint: `${process.env.GRAPHQL_URI}/subscriptions/auth`,
      transport: 'ajax',
      headers: {
        authorization: `Bearer ${getUserToken()}`,
      },
    },
    cluster: process.env.PUSHER_APP_CLUSTER,
  }),
});

// Cache implementation
const cache = new InMemoryCache();

// Token middleware
const authMiddleware = new ApolloLink((operation, forward) => {
  // add the authorization to the headers
  operation.setContext(
    merge(
      {
        headers: {
          Authorization: `Bearer ${getUserToken()}`,
        },
        uri: process.env.GRAPHQL_URI,
      },
      operation.getContext(),
    ),
  );

  return forward(operation);
});

// Handle GraphQL errors
const errorHandlingMiddleware = onError((errorResponse) => {
  const { graphQLErrors, networkError } = errorResponse;

  if (graphQLErrors) {
    // Lighthouse no longer sends an error category, @guard fails with this message
    const unauthenticatedError = graphQLErrors.find(
      (error) => error.message === 'Unauthenticated.',
    );

    if (unauthenticatedError) {
      if (process.env.MOBILE_BUILD) {
        $router.push({
          name: 'code',
        });
      } else {
        const path = window.location.pathname;

        $store.dispatch('app/logout').then(() => {
          $router.push({ name: 'login', query: { next: path } });
        });
      }
    }
  }
  if (networkError) {
    $store.dispatch('app/checkAppHealth');
  }
});

// Clean __typename from requests
const cleanTypeName = new ApolloLink((operation, forward) => {
  if (operation.variables) {
    operation.variables = omitDeep(cloneDeep(operation.variables), ['__typename']);
  }
  return forward(operation).map((data) => data);
});

// Create the apollo client
const apolloClient = new ApolloClient({
  link: ApolloLink.from([pusherLink, cleanTypeName, authMiddleware, errorHandlingMiddleware, httpLink]),
  cache,
  connectToDevTools: true,
});

const apolloProvider = createApolloProvider({
  defaultClient: apolloClient,
});

// Synchronization of long-term memory with temporary memory
const syncMobileData = () => {
  ['token', 'company_id', 'access_code', 'email'].forEach((key) => {
    appStorage.get(key).then((value) => {
      if (value) {
        localStorage.setItem(key, value);
      } else {
        localStorage.removeItem(key, value);
      }
    });
  });
};

export default ({ app, router, store }) => {
  $store = store;
  $router = router;
  app.use(apolloProvider);

  syncMobileData();
};

export { apolloClient };
